import { Link, useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { ArrowLeft, ArrowRight, Mic } from "lucide-react";
import { api, useContent, useMe, useSettings } from "@/lib/api";
import ApplyForm, { CITY_FIELDS, DEPT_FIELDS } from "@/components/site/ApplyForm";
import { STATUS, TYPE_LABEL } from "@/pages/Apply";
import { Chip, PageHero, Points, Skeleton, Wrap } from "@/night/ui";
import NotFound from "@/night/NotFound";

const TONE = { pending: "mute", accepted: "cyan", rejected: "red" };

function MyApps() {
  const { data: me } = useMe();
  const { data = [] } = useQuery({ queryKey: ["myapps"], queryFn: () => api.get("/applications/me").then((r) => r.data.items), enabled: !!me });
  if (!me || !data.length) return null;
  return (
    <div id="mine" className="mt-8 scroll-mt-28 border border-[var(--n-line)] bg-[var(--n-ink)] p-7" data-testid="my-applications">
      <h3 className="f-ar text-2xl">تقديماتي</h3>
      <div className="mt-5 divide-y divide-[var(--n-line)]">
        {data.map((a) => (
          <div key={a.id} className="flex items-center justify-between py-3 text-sm" data-testid={`my-app-${a.id}`}>
            <span className="font-bold">{TYPE_LABEL[a.type]}</span>
            <span className="flex items-center gap-3"><span className="f-mono text-xs text-slate-500">{a.created_at.slice(0, 10)}</span><Chip tone={TONE[a.status]}>{STATUS[a.status][0]}</Chip></span>
          </div>
        ))}
      </div>
    </div>
  );
}

export function Apply() {
  const { data: s } = useSettings();
  const { data: depts = [] } = useContent("departments");
  return (
    <>
      <PageHero en="ENLIST" ar="التقديم على المدينة" desc="الخطوة الأولى لقصتك. بعد ما تنقبل بالمدينة تقدر تقدّم على أي قسم." img="/site/sora-hero-eagle.jpg" />
      <Wrap className="mt-16 grid gap-12 lg:grid-cols-[1.3fr_1fr]">
        <div>
          <ApplyForm type="city" fields={CITY_FIELDS} next="/apply" closed={s && !s.city_apply_open} />
          <MyApps />
        </div>
        <div>
          <span className="kicker">FACTIONS</span>
          <h2 className="f-ar mt-3 text-4xl">التقديم على الأقسام</h2>
          <div className="mt-8 divide-y divide-[var(--n-line)] border-y border-[var(--n-line)]">
            {depts.map((d) => (
              <Link key={d.slug} to={`/apply/${d.slug}`} className="group flex items-center gap-5 py-4 transition-colors hover:bg-[var(--n-ink)]" data-testid={`apply-dept-link-${d.slug}`}>
                <img src={d.image} alt="" className="h-16 w-16 object-cover" />
                <div className="flex-1"><div className="f-ar text-xl">{d.name}</div><div className="f-lat text-xs tracking-[0.3em] text-[var(--n-cyan)]">{d.tag}</div></div>
                {d.soon ? <Chip tone="gold">قريباً</Chip> : !d.open ? <Chip tone="mute">مقفل</Chip> : <ArrowLeft size={20} className="text-[var(--n-cyan)] transition-transform group-hover:-translate-x-2" />}
              </Link>
            ))}
          </div>
        </div>
      </Wrap>
    </>
  );
}

export function ApplyDept() {
  const { slug } = useParams();
  const { data = [], isLoading } = useContent("departments");
  if (isLoading) return <Skeleton />;
  const d = data.find((x) => x.slug === slug);
  if (!d) return <NotFound />;
  return (
    <>
      <PageHero en={`ENLIST · ${d.tag}`} ar={`التقديم على ${d.name}`} img={d.image}>
        <Link to="/apply" className="btn-ghost mt-8" data-testid="apply-dept-back"><ArrowRight size={16} />كل التقديمات</Link>
      </PageHero>
      <Wrap className="mt-16 grid gap-12 lg:grid-cols-[1fr_1.3fr]">
        <div>
          <div className="border border-[var(--n-line)] bg-[var(--n-ink)] p-8" data-testid="apply-dept-requirements">
            <h2 className="f-ar text-3xl">شروط التقديم</h2>
            <Points items={d.requirements} className="mt-6" />
          </div>
          <div className="mt-4 flex flex-wrap gap-3">
            <Link to={`/rules/${d.rulesSlug}`} className="btn-ghost" data-testid="apply-dept-rules">قوانين القسم</Link>
            {d.voiceUrl && <a href={d.voiceUrl} target="_blank" rel="noreferrer" className="btn-ghost" data-testid="apply-dept-voice"><Mic size={15} />روم المقابلة</a>}
          </div>
        </div>
        <ApplyForm type={d.slug} fields={DEPT_FIELDS} next={`/apply/${d.slug}`} closed={!d.open || d.soon} />
      </Wrap>
    </>
  );
}
